import { faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import ModalDownloadingDocument from 'akasia-components/Contabilidad/centro-xml/common/ModalDownloadingDocument';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import SelectFacturaModal from './SelectFacturaModal';
import { useToast } from 'context/providers/ToastProvider';

const computeAniomesFromDate = (dateValue) => {
    try {
        if (!dateValue) return null;
        const d = new Date(dateValue);
        if (isNaN(d.getTime())) return null;
        const y = d.getFullYear();
        const m = (d.getMonth() + 1).toString().padStart(2, '0');
        return `${y}${m}`;
    } catch {
        return null;
    }
};

const FacturaDropdown = ({
    idOrden,
    orden,
    setOrden
}) => {
    const navigate = useNavigate();
    const { token, setToken } = useToken();
    const [facturas, setFacturas] = useState([]);
    const { showErrorToast, showSuccessToast } = useToast();
    // Modal state
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [modalAction, setModalAction] = useState(null); // 'visualize' | 'download'
    const [selectedIndex, setSelectedIndex] = useState(0);
    // Loading modal state
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadType, setDownloadType] = useState(''); // 'PDF' | 'XML'

    const fetchFacturas = async () => {
        if (!idOrden || idOrden <= 0) {
            setFacturas([]);
            return;
        }

        try {
            const response = await fetch('/api/contabilidad/facturas/FacturaOrden/ObtenerFacturasDeOrden', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idOrden })
            });

            const data = await response.json().catch(() => null);

            if (response.ok) {
                if (data && data.iResponseType === 1) {
                    try {
                        const detalles = data.detalles ? JSON.parse(data.detalles) : [];
                        setFacturas(Array.isArray(detalles) ? detalles : []);

                    } catch {
                        setFacturas(data.detalles || []);
                    }
                } else {
                    setFacturas([]);
                    showErrorToast(data?.vMessage || 'No se pudieron obtener las facturas');
                    
                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    navigate('/');
                    return;
                }
                setFacturas([]);
                showErrorToast(data?.vMessage || `Error al obtener facturas: ${response.status}`);
                
            }
        } catch (error) {
            setFacturas([]);
            showErrorToast('Error al consultar facturas: ' + error);
            
        }
    };

    useEffect(() => {
        fetchFacturas();
    }, [idOrden]);

    useEffect(() => {

        // Si ninguna factura está activa, marcar la orden como no facturada
        if (facturas.every(f => !f.bIsActive)) {
            setOrden({ ...orden, bIsFacturado: false });
        }


    }, [facturas]);

    const handleDescargarPDF = () => {
        if (!facturas || facturas.length === 0) {
            showErrorToast('No hay facturas para descargar PDF.');
            return;
        }
        if (facturas.length === 1) {
            if (!facturas[0].bIsActive) {
                // Si la factura está cancelada, mostrar modal para seleccionar
                openSelectModal('downloadPDF');
                return;
            }
            // download single directly
            downloadSinglePDF(facturas[0]);
            return;
        }
        // multiple -> open modal
        openSelectModal('downloadPDF');
    };

    const openSelectModal = (action) => {
        if (!facturas || facturas.length === 0) {
            showErrorToast('No hay facturas disponibles');
            
            return;
        }
        setModalAction(action);
        setSelectedIndex(0);
        setShowSelectModal(true);
    };

    const handleVisualizarFactura = () => {
        if (!facturas || facturas.length === 0) {
            showErrorToast('No hay facturas para visualizar.');
            
            return;
        }


        if (facturas.length === 1) {
            if (!facturas[0].bIsActive) {
                // Si la factura está cancelada, mostrar modal para seleccionar
                openSelectModal('visualize');
                return;
            }
            const primera = facturas[0];
            const idFactura = primera?.idFacturaEmitida;
            if (!idFactura) {
                showErrorToast('No se pudo obtener el id de la factura a visualizar.');
                
                return;
            }
            navigate(`/contabilidad/centro-xml/facturas-emitidas/visualizacion/${idFactura}`);
            return;
        }

        // multiple -> open modal
        openSelectModal('visualize');
    };

    const downloadSingleFactura = async (f) => {
        setDownloadType('xml');
        setShowDownloadModal(true);
        try {
            // Use the confirmed field names
            const uuid = f?.vUUIDFactura;
            const fecha = f?.dFechaEmision;
            const aniomes = computeAniomesFromDate(fecha);
            const rfc = f?.vRfcEmisor;

            if (!uuid || !aniomes) {
                setShowDownloadModal(false);
                showErrorToast('No se pudo determinar UUID o AñoMes para la factura seleccionada.');
                
                return;
            }

            let url = `/api/contabilidad/Facturacion/DescargarXmlFactura?uuid=${encodeURIComponent(uuid)}&aniomes=${encodeURIComponent(aniomes)}`;
            if (rfc) url += `&rfc=${encodeURIComponent(rfc)}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token }
            });

            if (response.ok) {
                const text = await response.text();
                const blob = new Blob([text], { type: 'application/xml' });
                const downloadName = `${uuid}.xml`;
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = downloadName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                setShowDownloadModal(false);
                showSuccessToast('Descarga iniciada');
            } else {
                setShowDownloadModal(false);
                if (response.status === 401) {
                    setToken(null);
                    navigate('/');
                    return;
                }
                const err = await response.json().catch(() => null);
                showErrorToast(err?.vMessage || `Error al descargar XML: ${response.status}`);
                
            }
        } catch (error) {
            setShowDownloadModal(false);
            showErrorToast('Error al descargar XML: ' + error);
            
        }
    };

    const downloadSinglePDF = async (f) => {
        setDownloadType('pdf');
        setShowDownloadModal(true);
        try {
            const idFactura = f?.idFacturaEmitida;
            if (!idFactura) {
                setShowDownloadModal(false);
                showErrorToast('No se pudo obtener el id de la factura para descargar PDF.');
                return;
            }

            const url = `/api/contabilidad/Facturacion/GenerarPdfFactura?idFactura=${encodeURIComponent(idFactura)}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token }
            });

            if (response.ok) {
                const blob = await response.blob();
                const downloadName = `Factura_${idFactura}.pdf`;
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = downloadName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                setShowDownloadModal(false);
                showSuccessToast('Descarga de PDF iniciada');
            } else {
                setShowDownloadModal(false);
                if (response.status === 401) {
                    setToken(null);
                    navigate('/');
                    return;
                }
                const err = await response.json().catch(() => null);
                showErrorToast(err?.vMessage || `Error al descargar PDF: ${response.status}`);
            }
        } catch (error) {
            setShowDownloadModal(false);
            showErrorToast('Error al descargar PDF: ' + error);
        }
    };

    const handleDescargarXMLClick = () => {
        if (!facturas || facturas.length === 0) {
            showErrorToast('No hay facturas para descargar.');
            
            return;
        }
        if (facturas.length === 1) {
            if (!facturas[0].bIsActive) {
                // Si la factura está cancelada, mostrar modal para seleccionar
                openSelectModal('download');
                return;
            }
            // download single directly
            downloadSingleFactura(facturas[0]);
            return;
        }
        // multiple -> open modal
        openSelectModal('download');
    };

    const handleModalConfirm = async () => {
        const f = facturas[selectedIndex];
        if (!f) return;
        if (modalAction === 'visualize') {
            const idFactura = f?.idFacturaEmitida;
            if (!idFactura) {
                showErrorToast('No se pudo obtener id de la factura seleccionada');
                
            } else {
                setShowSelectModal(false);
                navigate(`/contabilidad/centro-xml/facturas-emitidas/visualizacion/${idFactura}`);
            }
        } else if (modalAction === 'download') {
            setShowSelectModal(false);
            await downloadSingleFactura(f);
        } else if (modalAction === 'downloadPDF') {
            setShowSelectModal(false);
            await downloadSinglePDF(f);
        }
    };

    const titleText = `Factura${facturas && facturas.length > 0 ? ` (${facturas.length})` : ''}`;

    return (
        facturas && facturas.length > 0 &&
        <>
           
            <DropdownButton
                className="me-2 mb-1"
                variant="success"
                title={titleText}
                size="sm"
            >
                <Dropdown.Item onClick={handleDescargarPDF}>
                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                    Descargar PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={handleDescargarXMLClick}>
                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                    Descargar XML
                </Dropdown.Item>
                <Dropdown.Item onClick={handleVisualizarFactura}>
                    <FontAwesomeIcon icon={faEye} className="me-2" />
                    Visualizar factura
                </Dropdown.Item>
            </DropdownButton>

            <SelectFacturaModal
                show={showSelectModal}
                onHide={() => setShowSelectModal(false)}
                facturas={facturas}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                onConfirm={handleModalConfirm}
            />

            <ModalDownloadingDocument 
                show={showDownloadModal} 
                documentType={downloadType}
                title={downloadType === 'pdf' ? 'Generando PDF' : 'Descargando XML'}
            />
        </>
    );
};

FacturaDropdown.propTypes = {
    idOrden: PropTypes.number,
    orden: PropTypes.object,
    setOrden: PropTypes.func.isRequired
};

export default FacturaDropdown;
